import {type ConnectionOperation, DOCUMENT_FIELDS_PATH} from '@github-ui/optimistic-connection'
import {
  createConnectionMutationId,
  dispatchConnectionMutation,
} from '@github-ui/optimistic-connection/connectionMutationBridge'
import {commitMutation, graphql} from 'react-relay'
import type {Environment} from 'relay-runtime'

import type {
  IssueTypeColor,
  UpdateIssueIssueTypeInput,
  updateIssueIssueTypeSharedMutation,
  updateIssueIssueTypeSharedMutation$data,
  updateIssueIssueTypeSharedMutation$rawResponse,
} from './__generated__/updateIssueIssueTypeSharedMutation.graphql'

// eslint-disable-next-line relay/no-future-added-value
type IssueFieldDataType = 'DATE' | 'MULTI_SELECT' | 'NUMBER' | 'SINGLE_SELECT' | 'TEXT' | '%future added value'

type BaseIssueField = {
  readonly dataType: IssueFieldDataType
  readonly id: string
  readonly name: string
}

type OptimisticPinnedField =
  | (BaseIssueField & {readonly __typename: 'IssueFieldDate'})
  | (BaseIssueField & {readonly __typename: 'IssueFieldMultiSelect'})
  | (BaseIssueField & {readonly __typename: 'IssueFieldNumber'})
  | (BaseIssueField & {readonly __typename: 'IssueFieldSingleSelect'})
  | (BaseIssueField & {readonly __typename: 'IssueFieldText'})
  | {readonly __typename: 'IssueFieldMultiSelect'}
  | {readonly __typename: '%other'}

export type OptimisticIssueType = {
  id: string
  name: string
  description: string | null | undefined
  color: IssueTypeColor
  isEnabled?: boolean
  pinnedFields?: readonly OptimisticPinnedField[] | null
}

/**
 * Builds an optimistic response for the updateIssueIssueType mutation.
 * This allows the UI to update immediately without waiting for the server response.
 */
export function buildOptimisticResponse(
  issueId: string,
  issueType: OptimisticIssueType | null,
): updateIssueIssueTypeSharedMutation$rawResponse {
  return {
    updateIssueIssueType: {
      issue: {
        id: issueId,
        issueType: issueType
          ? {
              id: issueType.id,
              name: issueType.name,
              description: issueType.description ?? null,
              color: issueType.color,
              isEnabled: issueType.isEnabled ?? true,
              pinnedFields: (issueType.pinnedFields ?? []).map(field => {
                if (field.__typename === '%other' || field.__typename === 'IssueFieldMultiSelect') {
                  // For unknown types and IssueFieldMultiSelect (no fields selected),
                  // we need __isNode and id for the raw response
                  return {__typename: '%other' as const, __isNode: '%other' as const, id: ''}
                }
                return {
                  __typename: field.__typename,
                  __isNode: field.__typename,
                  id: field.id,
                  name: field.name,
                  dataType: field.dataType,
                }
              }),
            }
          : null,
      },
    },
  }
}

function toIssueTypeOperation(issueId: string, issueType: unknown): ConnectionOperation {
  return {type: 'setFields', id: issueId, fields: {issueType: issueType ?? null}}
}

export function commitUpdateIssueIssueTypeMutation({
  environment,
  input,
  optimisticIssueType,
  onError,
  onCompleted,
}: {
  environment: Environment
  input: UpdateIssueIssueTypeInput
  /** The full issue type object to use for optimistic updates. If provided, the UI will update immediately. */
  optimisticIssueType?: OptimisticIssueType | null
  onError?: (error: Error) => void
  onCompleted?: (response: updateIssueIssueTypeSharedMutation$data) => void
}) {
  // Only when Relay is applying one too: `undefined` means "no optimistic response", while `null`
  // is a real value meaning "clear the type".
  const mutationId = createConnectionMutationId()

  const dispatchTerminalError = (error: Error) => {
    dispatchConnectionMutation({phase: 'error', mutationId, error})
  }
  if (optimisticIssueType !== undefined) {
    dispatchConnectionMutation({
      phase: 'optimistic',
      mutationId,
      ownerId: input.issueId,
      connectionPath: DOCUMENT_FIELDS_PATH,
      operation: toIssueTypeOperation(input.issueId, optimisticIssueType),
    })
  }

  let disposable
  try {
    disposable = commitMutation<updateIssueIssueTypeSharedMutation>(environment, {
      // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
      mutation: graphql`
        mutation updateIssueIssueTypeSharedMutation($input: UpdateIssueIssueTypeInput!) @raw_response_type {
          updateIssueIssueType(input: $input) {
            issue {
              issueType {
                id
                name
                isEnabled
                description
                color
                pinnedFields {
                  __typename
                  ... on IssueFieldText {
                    id
                    name
                    dataType
                  }
                  ... on IssueFieldSingleSelect {
                    id
                    name
                    dataType
                  }
                  ... on IssueFieldDate {
                    id
                    name
                    dataType
                  }
                  ... on IssueFieldNumber {
                    id
                    name
                    dataType
                  }
                  ... on IssueFieldMultiSelect {
                    id
                    name
                    dataType
                  }
                }
              }
            }
          }
        }
      `,
      variables: {
        input,
      },
      optimisticResponse:
        optimisticIssueType !== undefined ? buildOptimisticResponse(input.issueId, optimisticIssueType) : undefined,
      onError: error => {
        dispatchTerminalError(error)
        onError?.(error)
      },
      onUnsubscribe: () => {
        dispatchTerminalError(new Error('Updating the issue type was cancelled'))
      },
      onCompleted: (response, errors) => {
        const issue = response.updateIssueIssueType?.issue
        // `issueType` is nullable, so a partial error can null it; publishing that while *setting*
        // a type would clear the row.
        const typeMissing = input.issueTypeId != null && issue?.issueType == null
        if (!issue || typeMissing) {
          const error = new Error(errors?.[0]?.message ?? 'Failed to update issue type')
          dispatchTerminalError(error)
          onError?.(error)
          return
        }

        dispatchConnectionMutation({
          phase: 'completed',
          mutationId,
          ownerId: input.issueId,
          connectionPath: DOCUMENT_FIELDS_PATH,
          operation: toIssueTypeOperation(input.issueId, issue.issueType),
        })
        onCompleted?.(response)
      },
    })
  } catch (error) {
    dispatchTerminalError(error instanceof Error ? error : new Error('Failed to update issue type'))
    throw error
  }

  return disposable
}
